import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Space,
  message,
  Row,
  Col,
  Card,
  Statistic,
  Tabs,
  Upload,
} from 'antd';
import {
  CloudUploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  InboxOutlined,
  ThunderboltOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import { firmware_api } from '../api/firmware-api';
import { inverter_model_api } from '../api/inverter-model-api';
import { axiosClient } from '../api/axios_client';

const TARGETS = [
  { id: 'arm-stm32g4', label: 'ARM — STM32G4', chip: 'STM32G4' },
  { id: 'dsp-esp32', label: 'DSP — ESP32', chip: 'ESP32' },
  { id: 'esp32-bootloader', label: 'Bootloader — ESP32', chip: 'ESP32' },
];

const UPDATE_POLICIES = [
  { value: 'urgent', label: 'Cần cập nhật gấp' },
  { value: 'scheduled_midnight', label: 'Cập nhật lúc 00:00' },
  { value: 'manual', label: 'Cập nhật thủ công' },
];

function formatBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function Firmware() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState('dsp-esp32');
  const [modalOpen, setModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState(null);
  const [form] = Form.useForm();

  const load = async (nextTarget = target) => {
    try {
      setLoading(true);
      const res = await firmware_api.list({
        ...(modelId ? { modelId } : {}),
      });
      if (res.success) {
        setItems(res.data || []);
      }
    } catch (error) {
      message.error(error.message || 'Không tải được danh sách firmware');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    inverter_model_api.list({ status: 'active' }).then((res) => {
      if (res.success) {
        setModels(res.data || []);
      }
    }).catch(() => undefined);
  }, []);

  useEffect(() => {
    load();
  }, [modelId]);

  const filtered = useMemo(
    () => items.filter((item) => item.target === target),
    [items, target]
  );

  const latest = filtered.find((item) => item.isLatest) || filtered[0];

  const handleUpload = async (values) => {
    if (!fileList[0]) {
      message.warning('Chọn file .bin / .hex / .elf');
      return;
    }
    const formData = new FormData();
    formData.append('file', fileList[0].originFileObj || fileList[0]);
    formData.append('target', values.target);
    formData.append('modelId', String(values.modelId));
    formData.append('version', values.version);
    formData.append('updatePolicy', values.updatePolicy);
    formData.append('notes', values.notes || '');
    formData.append('isLatest', 'true');
    try {
      setUploading(true);
      const res = await firmware_api.upload(formData);
      if (res.success) {
        message.success('Đã tải firmware lên');
        setModalOpen(false);
        form.resetFields();
        setFileList([]);
        await load();
      } else {
        message.error(res.message || 'Upload thất bại');
      }
    } catch (error) {
      message.error(error.message || error.response?.data?.message || 'Upload thất bại');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (record) => {
    try {
      const response = await axiosClient.get(`/api/firmware/${record.id}/download`, {
        responseType: 'blob',
      });
      const blob = new Blob([response]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = record.originalName || 'firmware.bin';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (_error) {
      window.open(`/api/firmware/${record.id}/download`, '_blank');
    }
  };

  const handleLatest = async (record) => {
    try {
      const res = await firmware_api.update(record.id, { isLatest: true });
      if (res.success) {
        message.success('Đã đặt làm bản phát hành');
        load();
      }
    } catch (error) {
      message.error(error.message || 'Không cập nhật được');
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: `Xóa firmware ${record.version}?`,
      content: record.originalName,
      okType: 'danger',
      onOk: async () => {
        const res = await firmware_api.remove(record.id);
        if (res.success) {
          setItems((prev) => prev.filter((item) => String(item.id) !== String(record.id)));
          message.success('Đã xóa');
          await load();
        } else {
          message.error(res.message || 'Xóa thất bại');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Version',
      dataIndex: 'version',
      render: (value, row) => (
        <Space>
          <strong>{value}</strong>
          {row.isLatest ? <Tag color="green">latest</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Model',
      dataIndex: 'modelCode',
      render: (value, row) => value || row.modelName || '—',
    },
    {
      title: 'Loại cập nhật',
      dataIndex: 'updatePolicy',
      render: (value, row) => {
        const color =
          value === 'urgent' ? 'red' : value === 'scheduled_midnight' ? 'orange' : 'default';
        return <Tag color={color}>{row.updatePolicyLabel || value}</Tag>;
      },
    },
    { title: 'File', dataIndex: 'originalName' },
    {
      title: 'Dung lượng',
      dataIndex: 'sizeBytes',
      render: formatBytes,
    },
    {
      title: 'SHA-256',
      dataIndex: 'sha256',
      ellipsis: true,
      render: (value) => <code style={{ fontSize: 12 }}>{value}</code>,
    },
    { title: 'Ghi chú', dataIndex: 'notes', ellipsis: true },
    { title: 'Người tải', dataIndex: 'uploadedBy' },
    {
      title: 'Ngày',
      dataIndex: 'createdAt',
      render: (value) => (value ? new Date(value).toLocaleString('vi-VN') : ''),
    },
    {
      title: '',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<DownloadOutlined />} onClick={() => handleDownload(record)}>
            Tải
          </Button>
          {!record.isLatest && (
            <Button size="small" icon={<CheckCircleOutlined />} onClick={() => handleLatest(record)}>
              Latest
            </Button>
          )}
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
        </Space>
      ),
    },
  ];

  return (
    <div>
      <h1 style={{ marginBottom: 16 }}>Firmware theo model</h1>
      <Space style={{ marginBottom: 16 }}>
        <span>Lọc model:</span>
        <Select
          allowClear
          placeholder="Tất cả model"
          style={{ minWidth: 220 }}
          value={modelId}
          onChange={setModelId}
          options={models.map((item) => ({
            value: item.id,
            label: `${item.code} — ${item.name}`,
          }))}
        />
      </Space>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Đích hiện tại"
              value={TARGETS.find((item) => item.id === target)?.label}
              prefix={target === 'dsp-esp32' ? <ApiOutlined /> : <ThunderboltOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="Số bản" value={filtered.length} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="Latest" value={latest?.version || '—'} />
          </Card>
        </Col>
      </Row>

      <Tabs
        activeKey={target}
        onChange={setTarget}
        tabBarExtraContent={
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            onClick={() => {
              form.setFieldsValue({
                target,
                version: '',
                notes: '',
                modelId: modelId || models[0]?.id,
                updatePolicy: 'manual',
              });
              setFileList([]);
              setModalOpen(true);
            }}
          >
            Tải firmware lên
          </Button>
        }
        items={TARGETS.map((item) => ({
          key: item.id,
          label: item.label,
          children: (
            <Table
              rowKey="id"
              loading={loading}
              columns={columns}
              dataSource={filtered}
              pagination={{ pageSize: 10 }}
            />
          ),
        }))}
      />

      <Modal
        title="Tải firmware"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleUpload} initialValues={{ target, updatePolicy: 'manual' }}>
          <Form.Item name="modelId" label="Model máy" rules={[{ required: true, message: 'Chọn model' }]}>
            <Select
              options={models.map((item) => ({
                value: item.id,
                label: `${item.code} — ${item.name}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="target" label="Chip / đích" rules={[{ required: true }]}>
            <Select
              options={TARGETS.map((item) => ({ value: item.id, label: item.label }))}
            />
          </Form.Item>
          <Form.Item name="version" label="Version" rules={[{ required: true, message: 'Nhập version, vd 1.0.3' }]}>
            <Input placeholder="1.0.3" />
          </Form.Item>
          <Form.Item name="updatePolicy" label="Loại hình cập nhật" rules={[{ required: true }]}>
            <Select options={UPDATE_POLICIES} />
          </Form.Item>
          <Form.Item label="File">
            <Upload.Dragger
              maxCount={1}
              fileList={fileList}
              beforeUpload={(file) => {
                setFileList([{ uid: file.uid || '-1', name: file.name, status: 'done', originFileObj: file }]);
                return false;
              }}
              onRemove={() => setFileList([])}
              accept=".bin,.hex,.elf,.dfu"
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p>Kéo thả .bin / .hex / .elf hoặc bấm chọn</p>
            </Upload.Dragger>
          </Form.Item>
          <Form.Item name="notes" label="Ghi chú">
            <Input.TextArea rows={3} placeholder="OTA signed, changelog..." />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={uploading} block icon={<CloudUploadOutlined />}>
            Lưu firmware
          </Button>
        </Form>
      </Modal>
    </div>
  );
}
